/**
 * "Where is this stuck?" chip — the middle column of every overdue candidate card.
 *
 * It replaces the old small "Blocked by:" line: the whole point of the Overdue tab
 * is answering "what is blocking this candidate and whose move is it?", so the
 * state gets a real labeled chip instead of grey micro-copy in the leftover
 * white space. One line per state, colors from the approved palette.
 */
import type { SlaClassification } from '../../types/taActionBoard';
import { COLORS, fmtDate } from './format';

interface StuckStyle {
  label: string;
  bg: string;
  text: string;
}

/** Grey/muted — used for states where nothing is due from us. */
const MUTED = { bg: COLORS.cotton, text: '#484848' };

const STUCK_STATES: Record<SlaClassification, StuckStyle> = {
  'awaiting-candidate': {
    label: 'Waiting for the candidate to book',
    bg: COLORS.orangeBg,
    text: COLORS.orangeText,
  },
  'blocked-by-decision': {
    label: 'Feedback in · your decision pending',
    bg: COLORS.blueBg,
    text: COLORS.blueText,
  },
  'feedback-missing': {
    label: 'Interview held · feedback missing',
    bg: COLORS.redBg,
    text: COLORS.redText,
  },
  'needs-case-panel': {
    label: 'Needs a case panel',
    bg: COLORS.orangeBg,
    text: COLORS.orangeText,
  },
  'invited-no-reply': {
    label: 'Invitation sent · no reply yet',
    bg: COLORS.orangeBg,
    text: COLORS.orangeText,
  },
  'no-next-step': {
    label: 'Moved on · no invitation sent yet',
    bg: COLORS.redBg,
    text: COLORS.redText,
  },
  // Snoozed carries its wake-up date (filled in by stuckState()).
  snoozed: { label: 'Snoozed', ...MUTED },
  // Booked candidates are summarized, never listed as cards — kept for exhaustiveness.
  booked: { label: 'Panel booked', ...MUTED },
};

/**
 * Resolve the stuck-state chip for a classification.
 * @param snoozedUntil ISO timestamp — required for `snoozed` to read
 *        "Snoozed until {date}".
 */
function stuckState(classification: SlaClassification, snoozedUntil?: string | null): StuckStyle {
  const base = STUCK_STATES[classification] ?? STUCK_STATES['no-next-step'];
  if (classification === 'snoozed') {
    return { ...base, label: `Snoozed until ${fmtDate(snoozedUntil ?? null)}` };
  }
  return base;
}

/**
 * The chip itself. Rendered in the card's middle column, so it flexes: it takes
 * the free space but never pushes the waiting pill or the phone chip off the row.
 *
 * **With `onGo` it IS the link to the work.** Naming the blocker and then leaving the recruiter
 * to find the tab, round, role and card that unblocks it was the gap (Theresa, Sep 2026). A `→`
 * marks the ones that travel; the rest stay plain `span`s, because some states have nowhere to
 * go (see slaChipTarget) and a chip that looks clickable and is not is worse than a label.
 */
export function SlaStuckChip({
  classification,
  snoozedUntil,
  onGo,
  goLabel,
}: {
  classification: SlaClassification;
  snoozedUntil?: string | null;
  /** Omit for states with no destination — the chip then renders as a plain label. */
  onGo?: () => void;
  /** Where it goes, for the title and the accessible name ("Go to Case scheduling"). */
  goLabel?: string;
}) {
  const { label, bg, text } = stuckState(classification, snoozedUntil);
  const className = 'inline-flex min-w-0 items-center rounded-full px-3 py-1.5 text-xs font-semibold';
  const style = { backgroundColor: bg, color: text };
  if (!onGo) {
    return (
      <span className={className} style={style}>
        <span className="truncate">{label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onGo}
      className={`${className} hover:opacity-80 focus-visible:outline focus-visible:outline-2`}
      style={style}
      title={goLabel}
      aria-label={goLabel ? `${label} — ${goLabel}` : label}
    >
      <span className="truncate">{label}</span>
      <span aria-hidden className="ml-1 shrink-0">
        →
      </span>
    </button>
  );
}
